import { useLazyQuery, useMutation } from '@apollo/client'
import { Formik, FormikHelpers } from 'formik'
import { isEmpty } from 'lodash'
import { ReactElement, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'

import { Button } from 'components/v2/Button'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import FormLayout from 'components/v2/FormLayout'
import Heading from 'components/v2/Heading'
import { RouteCard } from 'features/Services/components/NewServicesV2/components/RouteCard'
import { IRouteForm } from 'features/Services/components/NewServicesV2/components/RouteCard/types'
import { CREATE_ROUTE_V2 } from 'features/Routes/mutations-legacy'
import { GET_ROUTE_WITH_STOPS_V2 } from 'features/Routes/queries-legacy'
import { ADD_SEGMENT_COMPANY_LINE_SPEC, EDIT_SEGMENT } from 'features/Segments/mutations-legacy'
import {
  GTCreateTRouteResponse,
  GTRouteQueryInput,
  GTSegment,
  GTTPlaceSuggestionsResponse,
  GTTRoute
} from 'gql/types.generated'
import { getEmptyService, updateServiceByRouteAndRangedDate } from 'models/services'
import { schema } from './schema'
import { IRouteFormProps } from './types'
import { getAllSegmentsByRoute, ISegmentsHooksRoute } from 'features/Segments/hooks'
import { GET_SEGMENTS } from 'features/Segments/queries-legacy'
import { useGoogleMapsScript } from 'hooks/google-maps'
import { routeFormUtils } from 'features/Routes/utils'
import { encodeRouteShape, getGoogleDirectionsRoute, getTotalDistanceByRoute } from 'utils/google-maps'
import { IAutocompleteOutput } from 'components/v2/Autocomplete/types'
import { GET_PLACES_SUGGESTIONS } from 'features/Places/queries-legacy'

export const RouteForm = ({ service, onSubmit }: IRouteFormProps): ReactElement => {
  const intl = useIntl()
  const [error, setError] = useState<Error | null>(null)
  const [getRoute] = useLazyQuery<{ troute: GTTRoute }, GTRouteQueryInput>(GET_ROUTE_WITH_STOPS_V2, {
    fetchPolicy: 'network-only'
  })
  const [getSegmentsQuery] = useLazyQuery<{ segments: GTSegment[] }, { routeId: string }>(GET_SEGMENTS, {
    fetchPolicy: 'network-only'
  })
  const [getPlaces] = useLazyQuery<{ tplaceSuggestions: GTTPlaceSuggestionsResponse }>(GET_PLACES_SUGGESTIONS)
  const [createRoute] = useMutation<{ createTRoute: GTCreateTRouteResponse }>(CREATE_ROUTE_V2)
  const [addCompanyLineSpec] = useMutation(ADD_SEGMENT_COMPANY_LINE_SPEC)
  const [editSegment] = useMutation(EDIT_SEGMENT)
  useGoogleMapsScript()

  const createRouteFn = async ({
    stops,
    companyId,
    companyLineIds
  }: {
    stops: IAutocompleteOutput[]
    companyId: string
    companyLineIds: string[]
  }): Promise<ISegmentsHooksRoute> => {
    const stopIds = stops.map((stop) => stop.id)
    const placesResult = await getPlaces({
      variables: {
        input: {
          ids: stopIds
        }
      }
    })
    const directionsRoute = await getGoogleDirectionsRoute(
      routeFormUtils.mapStopIdsToRouteStops(stopIds, placesResult.data?.tplaceSuggestions.items || [])
    )
    const distance = getTotalDistanceByRoute(directionsRoute)
    const encodedShape = encodeRouteShape(directionsRoute)
    const { data } = await createRoute({
      variables: {
        input: {
          companyID: companyId,
          stopIDs: stopIds,
          companyLineIDs: companyLineIds,
          polyline: {
            encoded: encodedShape,
            distance: distance
          }
        }
      }
    })

    return {
      id: data?.createTRoute?.id ?? '',
      stops
    }
  }
  const handleSubmit = async (values: IRouteForm, { setSubmitting }: FormikHelpers<IRouteForm>): Promise<void> => {
    setError(null)
    const stopIds = values.stops.map((stop) => stop.id)
    try {
      const { data } = await getRoute({
        variables: { stopIDs: stopIds, companyID: values.company.id }
      })

      let route: ISegmentsHooksRoute
      if (!data?.troute) {
        route = await createRouteFn({
          stops: values.stops,
          companyId: values.company.id || '',
          companyLineIds: [values.companyLine.id]
        })
      } else {
        route = data.troute
      }
      const segments = await getAllSegmentsByRoute(route, getSegmentsQuery)
      for (const segment of segments) {
        const companyLineSpec = segment.companyLineSpecs.find(
          ({ companyLine }) => companyLine.id === values.companyLine.id
        )
        if (!companyLineSpec) {
          await addCompanyLineSpec({
            variables: {
              segmentId: segment.id,
              companyLineSpec: {
                companyLineID: values.companyLine.id,
                distance: 0,
                duration: 0
              }
            }
          })
        } else if (companyLineSpec.distance === null || companyLineSpec.duration === null) {
          await editSegment({
            variables: {
              segmentId: segment.id,
              companyLineSpecs: [
                {
                  companyLineID: values.companyLine.id,
                  distance: 0,
                  duration: 0
                }
              ]
            }
          })
        }
      }
      const services = isEmpty(service.services)
        ? [getEmptyService(route.id, values.stops)]
        : service.services.map((service) => updateServiceByRouteAndRangedDate(service, route.id, values.stops))
      onSubmit({ ...values, routeId: route.id, services })
    } catch (error) {
      // @ts-expect-error FIXME
      setError(error)
    }
    setSubmitting(false)
  }

  return (
    // @ts-expect-error FIXME
    <Formik initialValues={service} validationSchema={schema(intl)} onSubmit={handleSubmit}>
      {({ handleSubmit, isSubmitting }): ReactElement => {
        return (
          <FormLayout.Content as="form" onSubmit={handleSubmit}>
            <Heading.H3>
              <FormattedMessage id="trips.create.title" defaultMessage="Create trip" />
            </Heading.H3>
            <RouteCard title={<FormattedMessage id="trips.create.routeForm.title" defaultMessage="Select route" />} />
            {error && (
              <FormLayout.Actions>
                <ErrorMessage>
                  <FormattedMessage
                    id="trip.create.routeForm.error"
                    defaultMessage="There was an error while selecting the route"
                  />
                </ErrorMessage>
              </FormLayout.Actions>
            )}
            <FormLayout.Actions>
              <Button as={NavLink} kind="cancel" to="/trips" disabled={isSubmitting}>
                <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
              </Button>
              <Button type="submit" kind="primary" loading={isSubmitting}>
                <FormattedMessage id="dictionary.next" defaultMessage="Next" />
              </Button>
            </FormLayout.Actions>
          </FormLayout.Content>
        )
      }}
    </Formik>
  )
}
